import React from "react";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import Sparkline from "./Sparkline";

/**
 * Hero KPI card for dashboards. Big number + optional delta + optional mini viz.
 *
 * Props:
 *   label          text-label uppercase
 *   value          the metric (can be string like "$123.45" or a number)
 *   sub            small caption under the number (e.g. "3 late")
 *   delta          { value: number|string, direction: 'up'|'down'|'flat', label: string }
 *                  direction 'up' green, 'down' red, 'flat' muted
 *   sparkline      array of numbers or [{v}] for a mini line viz
 *   tone           'default' | 'accent' | 'danger' — subtle right-side hint stripe
 *   onClick        optional click handler
 *   testid         data-testid
 */
export default function HeroKPI({
    label,
    value,
    sub,
    delta,
    sparkline,
    tone = "default",
    onClick,
    testid,
}) {
    const clickable = typeof onClick === "function";
    const stripeColor = tone === "accent" ? "var(--asl-crimson)" : tone === "danger" ? "#DC2626" : "var(--asl-navy)";
    const sparklineColor = tone === "accent" ? "#C8102E" : tone === "danger" ? "#DC2626" : "#1E2A5A";
    const deltaEl = delta ? (
        <DeltaChip direction={delta.direction} value={delta.value} label={delta.label} />
    ) : null;
    const points = React.useMemo(() => {
        if (!Array.isArray(sparkline)) return [];
        return sparkline.map((p) => (typeof p === "number" ? { v: p } : p));
    }, [sparkline]);

    return (
        <div
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={clickable ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
            className={`asl-hero-kpi ${clickable ? "asl-hero-kpi--clickable" : ""}`}
            data-testid={testid}
        >
            <div className="asl-hero-kpi__stripe" style={{ background: stripeColor }} />
            <div className="asl-hero-kpi__body">
                <div className="asl-hero-kpi__row">
                    <div className="asl-hero-kpi__col-left">
                        <div className="text-label">{label}</div>
                        <div className="asl-hero-kpi__value mono">{value}</div>
                        {sub ? <div className="asl-hero-kpi__sub">{sub}</div> : null}
                        {deltaEl}
                    </div>
                    {points.length > 0 && (
                        <div className="asl-hero-kpi__viz">
                            <Sparkline data={points} color={sparklineColor} height={44} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function DeltaChip({ direction, value, label }) {
    const Icon = direction === "up" ? ArrowUpRight : direction === "down" ? ArrowDownRight : Minus;
    const color = direction === "up" ? "text-emerald-700 bg-emerald-50" : direction === "down" ? "text-rose-700 bg-rose-50" : "text-slate-600 bg-slate-50";
    return (
        <div className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-full mt-2 ${color}`}>
            <Icon className="h-3 w-3" />
            <span className="mono">{value}</span>
            {label ? <span className="text-[10px] font-normal opacity-80 ml-0.5">{label}</span> : null}
        </div>
    );
}
